/**
 * Option Catalogs Tab View
 * 
 * Integrated split-pane view for managing option catalogs directly in the tab.
 * Left pane: Filterable/sortable catalog list
 * Right pane: Catalog details and edit form
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Row,
  Col,
  Card,
  Table,
  Input,
  Button,
  Space,
  Form,
  Switch,
  Typography,
  Tag,
  Tooltip,
  Popconfirm,
  message,
  Spin,
  Divider,
  Descriptions,
  Tabs,
  List,
  InputNumber
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  SearchOutlined,
  SaveOutlined,
  CloseOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined
} from '@ant-design/icons';
import { apiClient } from '../../services/apiClient';
import { GlobalPropertiesService } from '../../services/api/globalPropertiesService';
import { PropertyDefinitionResponse } from '../../types/globalProperties';
import styles from './OptionCatalogsTabView.module.css';

const { Search } = Input;
const { TextArea } = Input;
const { Title, Text } = Typography;

interface OptionValue {
  id?: string;
  value: string;
  display_label?: string;
  description?: string;
  sequence_order: number;
  is_active: boolean;
}

interface OptionCatalog {
  id: string;
  name: string;
  description?: string;
  category?: string;
  display_label?: string;
  multiple_selection_allowed: boolean;
  values?: OptionValue[];
  created_at: string;
  updated_at?: string;
  isReferenced?: boolean; // Flag indicating if any global property references this catalog
}

type SortField = 'name' | 'category' | 'created_at' | null;
type SortOrder = 'ascend' | 'descend' | null;

export const OptionCatalogsTabView: React.FC = () => {
  const [catalogs, setCatalogs] = useState<OptionCatalog[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedCatalogId, setSelectedCatalogId] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('ascend');
  const [saving, setSaving] = useState(false);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState<number | null>(null);
  const [form] = Form.useForm();

  // Fetch catalogs and check which ones are referenced by global properties
  const fetchCatalogs = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch catalogs
      const response = await apiClient.get('/option-catalogs');
      const catalogsData: OptionCatalog[] = response.data || [];

      // Fetch all global properties to check which catalogs are referenced
      try {
        const propertiesResponse = await GlobalPropertiesService.listPropertyDefinitions({
          skip: 0,
          limit: 1000,
          summary: false
        });

        const properties = propertiesResponse.properties as PropertyDefinitionResponse[];
        
        // Create a set of catalog IDs that are referenced by properties
        const referencedCatalogIds = new Set<string>();
        properties.forEach(prop => {
          const catalogRef = prop.definition?.global_option_catalog_ref;
          if (catalogRef) {
            referencedCatalogIds.add(catalogRef);
          }
        });

        // Mark catalogs as referenced or not
        const catalogsWithReferenceFlag = catalogsData.map(catalog => ({
          ...catalog,
          isReferenced: referencedCatalogIds.has(catalog.id)
        }));

        setCatalogs(catalogsWithReferenceFlag);
      } catch (propsError: any) {
        console.warn('Error checking property references for catalogs:', propsError);
        // If we can't check references, still show catalogs without the flag
        setCatalogs(catalogsData);
      }
    } catch (error: any) {
      console.error('Error fetching catalogs:', error);
      message.error('Failed to fetch option catalogs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCatalogs();
  }, [fetchCatalogs]);

  // Filter and sort catalogs
  const filteredAndSortedCatalogs = useMemo(() => {
    let filtered = catalogs;

    // Apply search filter
    if (searchText) {
      const lowerSearch = searchText.toLowerCase();
      filtered = catalogs.filter(catalog =>
        catalog.name.toLowerCase().includes(lowerSearch) ||
        catalog.description?.toLowerCase().includes(lowerSearch) ||
        catalog.category?.toLowerCase().includes(lowerSearch)
      );
    }

    // Apply sorting
    if (sortField && sortOrder) {
      filtered = [...filtered].sort((a, b) => {
        let aVal: any;
        let bVal: any;

        switch (sortField) {
          case 'name':
            aVal = a.name;
            bVal = b.name;
            break;
          case 'category':
            aVal = a.category || '';
            bVal = b.category || '';
            break;
          case 'created_at':
            aVal = new Date(a.created_at).getTime();
            bVal = new Date(b.created_at).getTime();
            break;
          default:
            return 0;
        }

        if (aVal < bVal) return sortOrder === 'ascend' ? -1 : 1;
        if (aVal > bVal) return sortOrder === 'ascend' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [catalogs, searchText, sortField, sortOrder]);

  // Get selected catalog
  const selectedCatalog = useMemo(() => {
    if (!selectedCatalogId) return null;
    return catalogs.find(cat => cat.id === selectedCatalogId) || null;
  }, [catalogs, selectedCatalogId]);

  // Load catalog into form when selected
  useEffect(() => {
    if (selectedCatalog) {
      const sortedOptions = [...(selectedCatalog.values || [])].sort((a, b) => 
        (a.sequence_order || 0) - (b.sequence_order || 0)
      );
      form.setFieldsValue({
        name: selectedCatalog.name,
        description: selectedCatalog.description,
        category: selectedCatalog.category,
        display_label: selectedCatalog.display_label,
        multiple_selection_allowed: selectedCatalog.multiple_selection_allowed,
        options: sortedOptions.map((val) => ({
          id: val.id,
          value: val.value,
          display_label: val.display_label,
          description: val.description,
          sequence_order: val.sequence_order,
          is_active: val.is_active
        }))
      });
      setSelectedOptionIndex(null);
    } else {
      form.resetFields();
      setSelectedOptionIndex(null);
    }
  }, [selectedCatalog, form]);

  // Handle table row selection
  const handleRowClick = (record: OptionCatalog) => {
    setSelectedCatalogId(record.id);
  };

  // Handle create new catalog
  const handleCreateNew = () => {
    setSelectedCatalogId(null);
    form.resetFields();
    form.setFieldsValue({
      multiple_selection_allowed: false,
      options: []
    });
  };

  // Handle delete catalog
  const handleDeleteCatalog = async (catalogId: string) => {
    try {
      await apiClient.delete(`/option-catalogs/${catalogId}`);
      message.success('Catalog deleted successfully');
      if (selectedCatalogId === catalogId) {
        setSelectedCatalogId(null);
      }
      fetchCatalogs();
    } catch (error: any) {
      message.error('Failed to delete catalog: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Handle save catalog
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      // Get options from form - use formOptions (already tracked by Form.useWatch at component level)
      // formOptions is reactive and tracks changes to the 'options' field
      const formAllValues = form.getFieldsValue();
      
      let currentOptions: OptionValue[] = [];
      
      // Priority 1: Use formOptions from Form.useWatch (most reliable, reactive)
      if (formOptions && Array.isArray(formOptions) && formOptions.length > 0) {
        currentOptions = formOptions;
      }
      // Priority 2: Try form.getFieldsValue (setFieldsValue stores them here)
      else if (formAllValues.options && Array.isArray(formAllValues.options) && formAllValues.options.length > 0) {
        currentOptions = formAllValues.options;
      }
      // Priority 3: Fallback to getCurrentOptions
      else {
        currentOptions = getCurrentOptions();
      }
      
      console.log('[OptionCatalogsTabView] Saving catalog with options:', {
        optionsCount: currentOptions.length,
        options: currentOptions,
        formOptionsFromWatch: formOptions,
        formAllValues: formAllValues,
        formOptionsField: formAllValues.options,
        getCurrentOptionsResult: getCurrentOptions(),
        valuesOptions: values.options
      });
      
      // Prepare values array - ensure sequence_order is a number
      // Include ID for existing values when updating (backend uses ID for matching)
      const preparedValues = currentOptions
        .filter((opt: OptionValue) => opt.value && String(opt.value).trim())
        .map((opt: OptionValue, index: number) => {
          // Ensure sequence_order is a proper integer
          let seqOrder: number;
          if (typeof opt.sequence_order === 'number') {
            seqOrder = Math.floor(opt.sequence_order);
          } else if (opt.sequence_order !== undefined && opt.sequence_order !== null) {
            seqOrder = parseInt(String(opt.sequence_order), 10) || index;
          } else {
            seqOrder = index;
          }
          
          const valueObj: any = {
            value: String(opt.value).trim(),
            sequence_order: seqOrder,
            is_active: opt.is_active !== false && opt.is_active !== undefined
          };
          
          // Include ID if it exists (for updating existing values) - backend expects UUID string
          // Only include ID if it's a valid-looking UUID (not empty string or invalid)
          if (opt.id && String(opt.id).trim() && String(opt.id).match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
            valueObj.id = String(opt.id).trim();
          }
          
          // Only include optional fields if they have values (don't send empty strings)
          if (opt.display_label && String(opt.display_label).trim()) {
            valueObj.display_label = String(opt.display_label).trim();
          }
          if (opt.description && String(opt.description).trim()) {
            valueObj.description = String(opt.description).trim();
          }
          
          return valueObj;
        });
      
      console.log('[OptionCatalogsTabView] Prepared values for backend:', JSON.stringify(preparedValues, null, 2));

      const catalogData: any = {
        name: values.name,
        multiple_selection_allowed: values.multiple_selection_allowed || false
      };
      
      // Only include optional fields if they have values
      if (values.description && values.description.trim()) {
        catalogData.description = values.description.trim();
      }
      if (values.category && values.category.trim()) {
        catalogData.category = values.category.trim();
      }
      if (values.display_label && values.display_label.trim()) {
        catalogData.display_label = values.display_label.trim();
      }
      
      // Always include values array (even if empty)
      catalogData.values = preparedValues;
      
      console.log('[OptionCatalogsTabView] Catalog data to save:', catalogData);

      if (selectedCatalogId) {
        // Update existing
        await apiClient.put(`/option-catalogs/${selectedCatalogId}`, catalogData);
        message.success('Catalog updated successfully');
      } else {
        // Create new
        const response = await apiClient.post('/option-catalogs', catalogData);
        message.success('Catalog created successfully');
        setSelectedCatalogId(response.data.id);
      }

      fetchCatalogs();
    } catch (error: any) {
      if (error.errorFields) {
        // Validation errors from antd
        return;
      }
      console.error('[OptionCatalogsTabView] Error saving catalog:', error);
      console.error('[OptionCatalogsTabView] Error response:', error.response?.data);
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Unknown error';
      message.error(`Failed to save catalog: ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    if (selectedCatalog) {
      const sortedOptions = [...(selectedCatalog.values || [])].sort((a, b) => 
        (a.sequence_order || 0) - (b.sequence_order || 0)
      );
      // Reset to original values
      form.setFieldsValue({
        name: selectedCatalog.name,
        description: selectedCatalog.description,
        category: selectedCatalog.category,
        display_label: selectedCatalog.display_label,
        multiple_selection_allowed: selectedCatalog.multiple_selection_allowed,
        options: sortedOptions.map((val) => ({
          id: val.id,
          value: val.value,
          display_label: val.display_label,
          description: val.description,
          sequence_order: val.sequence_order,
          is_active: val.is_active
        }))
      });
      setSelectedOptionIndex(null);
    } else {
      form.resetFields();
      setSelectedOptionIndex(null);
    }
  };

  // Get current options from form, sorted by sequence_order
  const getCurrentOptions = (): OptionValue[] => {
    const options = form.getFieldValue('options') || [];
    return [...options].sort((a: OptionValue, b: OptionValue) => 
      (a.sequence_order || 0) - (b.sequence_order || 0)
    );
  };

  // Handle add new option
  const handleAddNewOption = () => {
    const currentOptions = getCurrentOptions();
    const nextNumber = currentOptions.length + 1;
    const maxSequence = currentOptions.length > 0
      ? Math.max(...currentOptions.map((opt: OptionValue) => opt.sequence_order || 0))
      : 0;

    const newOption: OptionValue = {
      value: `option_${nextNumber}`,
      display_label: `Option ${nextNumber}`,
      description: '',
      sequence_order: maxSequence + 1,
      is_active: true
    };

    const currentOptionsArray = form.getFieldValue('options') || [];
    const updatedOptionsArray = [...currentOptionsArray, newOption];
    
    // Update form with new options array - use both setFieldsValue and setFieldValue to ensure tracking
    form.setFieldsValue({
      options: updatedOptionsArray
    });
    form.setFieldValue('options', updatedOptionsArray);

    // Select the new option (it will be at the end after sorting)
    const updatedOptions = getCurrentOptions();
    const newIndex = updatedOptions.length - 1;
    setSelectedOptionIndex(newIndex);
  };

  // Handle move option up
  const handleMoveOptionUp = (index: number) => {
    if (index === 0) return;
    
    const options = getCurrentOptions();
    const option = options[index];
    const prevOption = options[index - 1];
    
    // Swap sequence orders
    const tempOrder = option.sequence_order;
    option.sequence_order = prevOption.sequence_order;
    prevOption.sequence_order = tempOrder;

    // Update form
    const currentOptionsArray = form.getFieldValue('options') || [];
    const updatedOptions = currentOptionsArray.map((opt: any) => {
      if (opt.value === option.value) return option;
      if (opt.value === prevOption.value) return prevOption;
      return opt;
    });

    form.setFieldsValue({ options: updatedOptions });
    setSelectedOptionIndex(index - 1);
  };

  // Handle move option down
  const handleMoveOptionDown = (index: number) => {
    const options = getCurrentOptions();
    if (index === options.length - 1) return;
    
    const option = options[index];
    const nextOption = options[index + 1];
    
    // Swap sequence orders
    const tempOrder = option.sequence_order;
    option.sequence_order = nextOption.sequence_order;
    nextOption.sequence_order = tempOrder;

    // Update form
    const currentOptionsArray = form.getFieldValue('options') || [];
    const updatedOptions = currentOptionsArray.map((opt: any) => {
      if (opt.value === option.value) return option;
      if (opt.value === nextOption.value) return nextOption;
      return opt;
    });

    form.setFieldsValue({ options: updatedOptions });
    setSelectedOptionIndex(index + 1);
  };

  // Handle delete option
  const handleDeleteOption = (index: number) => {
    const options = getCurrentOptions();
    const optionToDelete = options[index];
    
    const currentOptionsArray = form.getFieldValue('options') || [];
    const updatedOptions = currentOptionsArray.filter((opt: any) => 
      opt.value !== optionToDelete.value
    );

    form.setFieldsValue({ options: updatedOptions });
    
    // Select previous option or clear selection
    if (index > 0) {
      setSelectedOptionIndex(index - 1);
    } else if (updatedOptions.length > 0) {
      setSelectedOptionIndex(0);
    } else {
      setSelectedOptionIndex(null);
    }
  };

  // Get selected option - watch form values to update when options change
  const formOptions = Form.useWatch('options', form);
  const selectedOption = useMemo(() => {
    if (selectedOptionIndex === null) return null;
    const options = getCurrentOptions();
    return options[selectedOptionIndex] || null;
  }, [selectedOptionIndex, formOptions]);

  // Handle table sorting
  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    if (sorter.field) {
      setSortField(sorter.field);
      setSortOrder(sorter.order || null);
    }
  };

  // Table columns
  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      render: (text: string) => <strong>{text}</strong>
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text: string) => text || <Text type="secondary">-</Text>
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      sorter: true,
      render: (text: string) => text || <Text type="secondary">-</Text>
    },
    {
      title: 'Values',
      key: 'values',
      width: 100,
      render: (_: any, record: OptionCatalog) => (
        <Tag color="blue">{record.values?.length || 0} options</Tag>
      )
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      render: (_: any, record: OptionCatalog) => {
        if (record.isReferenced === undefined) {
          // Not checked yet
          return <Text type="secondary">-</Text>;
        }
        if (record.isReferenced) {
          return <Tag color="green">In Use</Tag>;
        } else {
          return (
            <Tooltip title="No global properties reference this option catalog">
              <Tag color="orange">Unused</Tag>
            </Tooltip>
          );
        }
      }
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: true,
      width: 120,
      render: (date: string) => new Date(date).toLocaleDateString()
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_: any, record: OptionCatalog) => (
        <span onClick={(e) => e.stopPropagation()}>
          <Popconfirm
            title="Delete this catalog?"
            onConfirm={() => handleDeleteCatalog(record.id)}
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </span>
      )
    }
  ];

  return (
    <div className="option-catalogs-tab-view">
      <Row gutter={16} style={{ height: '100%' }}>
        {/* Left Pane - Catalog List */}
        <Col xs={24} lg={14} xl={12}>
          <Card
            title={
              <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                <Title level={4} style={{ margin: 0 }}>Option Catalogs</Title>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleCreateNew}
                >
                  Create New
                </Button>
              </Space>
            }
            extra={
              <Search
                placeholder="Search catalogs..."
                allowClear
                style={{ width: 250 }}
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            }
          >
            <Table
              columns={columns}
              dataSource={filteredAndSortedCatalogs}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 10 }}
              onRow={(record) => ({
                onClick: () => handleRowClick(record),
                style: {
                  cursor: 'pointer',
                  backgroundColor: selectedCatalogId === record.id ? '#e6f7ff' : 'transparent'
                }
              })}
              onChange={handleTableChange}
              rowClassName={(record) =>
                selectedCatalogId === record.id ? 'selected-row' : ''
              }
            />
          </Card>
        </Col>

        {/* Right Pane - Catalog Details */}
        <Col xs={24} lg={10} xl={12}>
          <Card
            title={
              <Title level={4} style={{ margin: 0 }}>
                {selectedCatalogId ? 'Catalog Details' : 'Create New Catalog'}
              </Title>
            }
          >
            {selectedCatalogId && !selectedCatalog ? (
              <Spin />
            ) : selectedCatalogId === null || (selectedCatalogId && selectedCatalog) ? (
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSave}
                className={styles.catalogDetailsForm}
                initialValues={{ options: [] }}
              >
                {/* Hidden Form.Item to ensure options array is tracked by form */}
                <Form.Item name="options" noStyle>
                  <Input type="hidden" />
                </Form.Item>
                <Tabs
                  items={[
                    {
                      key: 'basic-info',
                      label: 'Basic Info',
                      children: (
                        <div className={styles.basicInfoTabContent}>
                          <Title level={5}>Basic Information</Title>
                          <Form.Item
                            label="Catalog Name"
                            name="name"
                            rules={[{ required: true, message: 'Please enter catalog name' }]}
                          >
                            <Input placeholder="e.g., Status Options, Currency Codes" />
                          </Form.Item>

                          <Form.Item
                            label="Description"
                            name="description"
                          >
                            <TextArea rows={2} placeholder="Describe the purpose of this catalog" />
                          </Form.Item>

                          <Form.Item
                            label="Category"
                            name="category"
                          >
                            <Input placeholder="Optional category for grouping" />
                          </Form.Item>

                          <Form.Item
                            label="Display Label"
                            name="display_label"
                          >
                            <Input placeholder="Label to display in UI" />
                          </Form.Item>

                          <Form.Item
                            label="Multiple Selection Allowed"
                            name="multiple_selection_allowed"
                            valuePropName="checked"
                          >
                            <Switch />
                          </Form.Item>

                          {selectedCatalog && (
                            <>
                              <Divider />

                              <Title level={5}>Metadata</Title>
                              <Descriptions column={1} size="small" bordered>
                                <Descriptions.Item label="Catalog ID">
                                  <Text code>{selectedCatalog.id}</Text>
                                </Descriptions.Item>
                                <Descriptions.Item label="Created At">
                                  {new Date(selectedCatalog.created_at).toLocaleString()}
                                </Descriptions.Item>
                                {selectedCatalog.updated_at && (
                                  <Descriptions.Item label="Updated At">
                                    {new Date(selectedCatalog.updated_at).toLocaleString()}
                                  </Descriptions.Item>
                                )}
                              </Descriptions>

                              <Divider />
                            </>
                          )}

                          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                            <Button onClick={handleCancel} icon={<CloseOutlined />}>
                              Cancel
                            </Button>
                            <Button
                              type="primary"
                              htmlType="submit"
                              loading={saving}
                              icon={<SaveOutlined />}
                            >
                              Save
                            </Button>
                          </Space>
                        </div>
                      )
                    },
                    {
                      key: 'options',
                      label: 'Options',
                      children: (
                        <div className={styles.optionsTabContent}>
                          <Row gutter={16} style={{ height: '100%' }}>
                            {/* Left Pane - Options List */}
                            <Col span={10}>
                              <Card
                                size="small"
                                title={
                                  <Button
                                    type="primary"
                                    size="small"
                                    icon={<PlusOutlined />}
                                    onClick={handleAddNewOption}
                                    block
                                  >
                                    Add New Option
                                  </Button>
                                }
                                className={styles.optionsListCard}
                              >
                                <List
                                  dataSource={getCurrentOptions()}
                                  size="small"
                                  className={styles.optionsList}
                                  renderItem={(option: OptionValue, index: number) => {
                                    const isSelected = selectedOptionIndex === index;
                                    return (
                                      <List.Item
                                        className={isSelected ? styles.selectedOptionItem : ''}
                                        onClick={() => setSelectedOptionIndex(index)}
                                        style={{
                                          cursor: 'pointer',
                                          backgroundColor: isSelected ? '#e6f7ff' : 'transparent',
                                          padding: '8px 12px'
                                        }}
                                        actions={[
                                          <Button
                                            key="up"
                                            type="text"
                                            size="small"
                                            icon={<ArrowUpOutlined />}
                                            disabled={index === 0}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleMoveOptionUp(index);
                                            }}
                                          />,
                                          <Button
                                            key="down"
                                            type="text"
                                            size="small"
                                            icon={<ArrowDownOutlined />}
                                            disabled={index === getCurrentOptions().length - 1}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleMoveOptionDown(index);
                                            }}
                                          />
                                        ]}
                                      >
                                        <List.Item.Meta
                                          title={
                                            <Space>
                                              <Text strong>{option.sequence_order || index + 1}.</Text>
                                              <Text>{option.value}</Text>
                                              {option.display_label && (
                                                <Text type="secondary">({option.display_label})</Text>
                                              )}
                                              {option.is_active && (
                                                <Tag color="green">Active</Tag>
                                              )}
                                            </Space>
                                          }
                                        />
                                      </List.Item>
                                    );
                                  }}
                                />
                              </Card>
                            </Col>

                            {/* Right Pane - Option Details */}
                            <Col span={14}>
                              <Card
                                size="small"
                                title={
                                  <Title level={5} style={{ margin: 0 }}>
                                    {selectedOptionIndex !== null ? 'Option Details' : 'Select an Option'}
                                  </Title>
                                }
                                className={styles.optionDetailsCard}
                              >
                                {selectedOptionIndex !== null && selectedOption ? (
                                  <Form.List name="options">
                                    {(fields) => {
                                      const currentOptions = getCurrentOptions();
                                      if (selectedOptionIndex === null || selectedOptionIndex >= currentOptions.length) {
                                        return null;
                                      }
                                      
                                      const selectedOptionValue = currentOptions[selectedOptionIndex].value;
                                      const fieldIndex = fields.findIndex((field) => {
                                        const fieldValue = form.getFieldValue(['options', field.name, 'value']);
                                        return fieldValue === selectedOptionValue;
                                      });

                                      if (fieldIndex === -1) {
                                        // Try to find by index if value matching fails
                                        if (selectedOptionIndex < fields.length) {
                                          const field = fields[selectedOptionIndex];
                                          return (
                                            <>
                                              <Form.Item
                                                name={[field.name, 'value']}
                                                label="Value"
                                                rules={[{ required: true, message: 'Required' }]}
                                              >
                                                <Input placeholder="Value" />
                                              </Form.Item>

                                              <Form.Item
                                                name={[field.name, 'display_label']}
                                                label="Display Label"
                                              >
                                                <Input placeholder="Display Label" />
                                              </Form.Item>

                                              <Form.Item
                                                name={[field.name, 'description']}
                                                label="Description"
                                              >
                                                <TextArea rows={3} placeholder="Description" />
                                              </Form.Item>

                                              <Row gutter={8}>
                                                <Col span={12}>
                                                  <Form.Item
                                                    name={[field.name, 'sequence_order']}
                                                    label="Sequence Order"
                                                  >
                                                    <InputNumber
                                                      min={1}
                                                      style={{ width: '100%' }}
                                                      placeholder="Order"
                                                    />
                                                  </Form.Item>
                                                </Col>
                                                <Col span={12}>
                                                  <Form.Item
                                                    name={[field.name, 'is_active']}
                                                    label="Active"
                                                    valuePropName="checked"
                                                  >
                                                    <Switch />
                                                  </Form.Item>
                                                </Col>
                                              </Row>

                                              <Divider />

                                              <Popconfirm
                                                title="Delete this option?"
                                                onConfirm={() => handleDeleteOption(selectedOptionIndex)}
                                              >
                                                <Button
                                                  type="primary"
                                                  danger
                                                  icon={<DeleteOutlined />}
                                                  block
                                                >
                                                  Delete Option
                                                </Button>
                                              </Popconfirm>
                                            </>
                                          );
                                        }
                                        return null;
                                      }
                                      
                                      const field = fields[fieldIndex];

                                      return (
                                        <>
                                          <Form.Item
                                            name={[field.name, 'value']}
                                            label="Value"
                                            rules={[{ required: true, message: 'Required' }]}
                                          >
                                            <Input placeholder="Value" />
                                          </Form.Item>

                                          <Form.Item
                                            name={[field.name, 'display_label']}
                                            label="Display Label"
                                          >
                                            <Input placeholder="Display Label" />
                                          </Form.Item>

                                          <Form.Item
                                            name={[field.name, 'description']}
                                            label="Description"
                                          >
                                            <TextArea rows={3} placeholder="Description" />
                                          </Form.Item>

                                          <Row gutter={8}>
                                            <Col span={12}>
                                              <Form.Item
                                                name={[field.name, 'sequence_order']}
                                                label="Sequence Order"
                                              >
                                                <InputNumber
                                                  min={1}
                                                  style={{ width: '100%' }}
                                                  placeholder="Order"
                                                />
                                              </Form.Item>
                                            </Col>
                                            <Col span={12}>
                                              <Form.Item
                                                name={[field.name, 'is_active']}
                                                label="Active"
                                                valuePropName="checked"
                                              >
                                                <Switch />
                                              </Form.Item>
                                            </Col>
                                          </Row>

                                          <Divider />

                                          <Popconfirm
                                            title="Delete this option?"
                                            onConfirm={() => handleDeleteOption(selectedOptionIndex)}
                                          >
                                            <Button
                                              type="primary"
                                              danger
                                              icon={<DeleteOutlined />}
                                              block
                                            >
                                              Delete Option
                                            </Button>
                                          </Popconfirm>
                                        </>
                                      );
                                    }}
                                  </Form.List>
                                ) : (
                                  <div style={{ textAlign: 'center', padding: '40px 0' }}>
                                    <Text type="secondary">
                                      Select an option from the list to view and edit its details,
                                      or click "Add New Option" to create one.
                                    </Text>
                                  </div>
                                )}
                              </Card>
                            </Col>
                          </Row>
                        </div>
                      )
                    }
                  ]}
                />
              </Form>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <Text type="secondary">
                  Select a catalog from the list to view and edit its details,
                  or click "Create New" to add a new catalog.
                </Text>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

